import Alert from 'common/components/Alert'
import Portal from 'common/components/Portal'
import theme from 'common/theme'
import { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'

interface ISnackbarProps {
  open: boolean
  type?: string
  side?: string
  sideTopStyle?: string
  position?: string
  onClose?: any
  autoHideDuration?: number
  disabledCloseAnimation?: boolean
  children: any
}

const fadeFromTop = keyframes`
  from {
      transform: translateY(-100vh);
    }
    to {
      transform: translateY(0);
    }
`
const fadeFromBottom = keyframes`
  from {
      transform: translateY(100vh);
    }
    to {
      transform: translateY(0);
    }
`

const setTransition = (open, side) => {
  if (open) {
    return 'transform: translateY(0);'
  }
  switch (side) {
    case 'top':
      return 'transform: translateY(-100vh);'
    default:
      return 'transform: translateY(100vh);'
  }
}

const StyledSnackbar = styled(Alert)`
  border-radius: ${theme.radius.base};
  box-shadow: ${theme.snackbar.boxShadow};
  margin: ${(props) =>
    props.side === 'top' ? '0 0 8px 0' : '8px 0 0 0'};
    0.2s ease-out;
  animation: ${(props) =>
    props.side === 'top' ? fadeFromTop : fadeFromBottom}
    0.2s ease-out;
  transition: transform 0.2s ease-in;
  ${(props) => setTransition(props.open, props.side)}
`

function Snackbar({
  open,
  type = '',
  side = 'top',
  sideTopStyle = '150px',
  position = 'center',
  onClose = null,
  autoHideDuration = null,
  disabledCloseAnimation = false,
  children,
  ...otherProps
}: ISnackbarProps): JSX.Element {
  const [rootContainer, setRootContainer] = useState(null)
  const [show, setShow] = useState<boolean>(open)

  const close = () => {
    if (disabledCloseAnimation) return setShow(false)
    return setTimeout(() => {
      setShow(false)
    }, 200)
  }

  useEffect(() => {
    const container = document.createElement('div')
    container.style.position = 'fixed'
    container.style.zIndex = `${theme.zIndex.snackbar}`
    switch (side) {
      case 'bottom':
        container.style.bottom = '30px'
        break
      default:
        container.style.top = sideTopStyle
        break
    }
    switch (position) {
      case 'left':
        container.style.left = '30px'
        break
      case 'right':
        container.style.right = '30px'
        break
      default:
        container.style.left = '50%'
        container.style.transform = 'translateX(-50%)'
        break
    }
    setRootContainer(container)
  }, [])

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (open) {
      setShow(true)
      if (autoHideDuration) {
        const hideDuration = setTimeout(() => {
          onClose()
          close()
        }, autoHideDuration)
        return () => clearTimeout(hideDuration)
      }
    } else {
      close()
    }
  }, [open])
  return (
    <>
      {show && (
        <Portal
          id={`snackbar-${side}-${position}`}
          container={rootContainer}
        >
          <StyledSnackbar
            data-testid="snackbar-component"
            type={type}
            open={open}
            side={side}
            position={position}
            onClose={onClose}
            autoHideDuration={autoHideDuration}
            {...otherProps}
          >
            {children}
          </StyledSnackbar>
        </Portal>
      )}
    </>
  )
}

export default Snackbar
